/**
 * Data-owning facility notes inbox.
 *
 * Translates toolbar state to the notes API and composes the production-sized
 * NotesView components. No note data is stored in browser persistence.
 */

import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "risa-oasis-ui_v2";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type {
  ClinicalNoteResponse,
  NoteAssigneeResponse,
} from "../api/clinicalTrialsApiTypes";
import { DEFAULT_NOTES_FILTERS, type NotesFilters } from "../types/notes";
import { NotesFiltersBar } from "../views/NotesView/NotesFiltersBar";
import { NotesTable } from "../views/NotesView/NotesTable";
import type { EligibilityWorklistReturnState } from "../views/PatientEligibilityMappingView/eligibilityTableState";

interface NotesRouteState {
  eligibilityReturnState?: EligibilityWorklistReturnState;
}

/** Keep overdue notes ahead of other due dates in the Due Date view. */
function sortNotesForDisplay(
  notes: ClinicalNoteResponse[],
  orderBy: NotesFilters["orderBy"],
): ClinicalNoteResponse[] {
  if (orderBy !== "due_date") return notes;
  return [...notes].sort((left, right) => {
    if (left.is_overdue !== right.is_overdue) {
      return left.is_overdue ? -1 : 1;
    }
    if (!left.due_date && right.due_date) return 1;
    if (left.due_date && !right.due_date) return -1;
    return (left.due_date ?? "").localeCompare(right.due_date ?? "");
  });
}

/**
 * Render and load the facility-scoped notes worklist.
 *
 * @returns API-backed notes inbox
 */
export function NotesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, user } = useAuth();
  const eligibilityReturnState = (location.state as NotesRouteState | null)
    ?.eligibilityReturnState;
  const [filters, setFilters] = useState<NotesFilters>(DEFAULT_NOTES_FILTERS);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [notes, setNotes] = useState<ClinicalNoteResponse[]>([]);
  const [assignees, setAssignees] = useState<NoteAssigneeResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedQuery(filters.query.trim()),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [filters.query]);

  useEffect(() => {
    const controller = new AbortController();
    clinicalTrialsService
      .getNoteAssignees(controller.signal)
      .then(setAssignees)
      .catch(() => setAssignees([]));
    return () => controller.abort();
  }, []);

  const apiQuery = useMemo(
    () => ({
      query: debouncedQuery || undefined,
      unread: filters.unreadOnly || undefined,
      assigned_to_me: filters.assignedToMe || undefined,
      assignee_id: filters.assigneeId || undefined,
      overdue: filters.overdueOnly || undefined,
      status: filters.status === "all" ? undefined : filters.status,
      order:
        filters.orderBy === "updated_at"
          ? ("recent_activity" as const)
          : filters.orderBy === "due_date"
            ? ("due_date" as const)
            : filters.orderDirection === "asc"
              ? ("oldest" as const)
              : ("newest" as const),
      page_size: 50,
    }),
    [debouncedQuery, filters],
  );

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    clinicalTrialsService
      .getNotes(apiQuery, controller.signal)
      .then((response) => {
        setNotes(sortNotesForDisplay(response.hits, filters.orderBy));
        setTotalCount(response.hits.length);
        setNextCursor(response.next_cursor ?? null);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            reason instanceof Error ? reason.message : "Failed to load notes",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiQuery, retry]);

  /**
   * Open a note on its patient eligibility detail screen. An explicit note
   * linkage takes precedence; otherwise use the trial selected before opening
   * the Notes worklist from Eligibility.
   *
   * @param note - Selected facility note
   * @returns Nothing
   */
  const openNote = (note: ClinicalNoteResponse) => {
    const params = new URLSearchParams({ notes: "open", noteId: note.note_id });
    const trialId =
      note.eligibility?.trial_id?.trim() ||
      eligibilityReturnState?.selectedTrialValue.trim();
    if (!trialId) {
      toast.error(
        "Open Notes from the Eligibility page to select a trial.",
        "Unable to open patient eligibility",
      );
      return;
    }
    navigate(
      `/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(note.mrn)}?${params.toString()}`,
      { state: location.state },
    );
  };

  /**
   * Append the next cursor page while preserving the active server filters.
   *
   * @returns Nothing
   */
  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const response = await clinicalTrialsService.getNotes({
        ...apiQuery,
        cursor: nextCursor,
      });
      setNotes((current) =>
        sortNotesForDisplay([...current, ...response.hits], filters.orderBy),
      );
      setTotalCount((count) => count + response.hits.length);
      setNextCursor(response.next_cursor ?? null);
    } catch (reason: unknown) {
      setError(
        reason instanceof Error ? reason.message : "Failed to load more notes",
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  const canManageMembers = Boolean(role?.permissions.can_manage_org_users);
  const hasEligibilityReturnTarget = Boolean(eligibilityReturnState);

  return (
    <main className="min-h-full bg-uiGray-50 px-6 py-6">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-5 flex items-start justify-between">
          <div className="flex items-start gap-4">
            {(user?.isSuperAdmin || hasEligibilityReturnTarget) && (
              <Button
                buttonType="secondary"
                size="medium"
                disabled={false}
                onClick={() =>
                  hasEligibilityReturnTarget
                    ? navigate("/eligibility", {
                        state: { eligibilityReturnState },
                      })
                    : navigate("/notifications")
                }
              >
                <span className="inline-flex items-center gap-2">
                  <ArrowLeft size={16} /> Back
                </span>
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-semibold">Notes</h1>
              <p className="mt-1 text-sm text-primaryGray-8">
                Facility collaboration notes across all clinical-trials patients
              </p>
            </div>
          </div>
          <span className="text-sm text-primaryGray-8">
            {totalCount} loaded
          </span>
        </header>
        <NotesFiltersBar
          filters={filters}
          assignees={assignees}
          onChange={setFilters}
        />
        {!assignees.length && !isLoading && !error && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
            No eligible assignees are available.{" "}
            {canManageMembers ? (
              <button
                type="button"
                onClick={() => navigate("/settings/members")}
                className="font-semibold underline"
              >
                Manage members
              </button>
            ) : (
              "Contact an administrator to add a clinical-trials member."
            )}
          </div>
        )}
        <NotesTable
          notes={notes}
          isLoading={isLoading}
          error={error}
          onOpen={openNote}
          onRetry={() => setRetry((value) => value + 1)}
        />
        {nextCursor && !isLoading && !error && (
          <div className="mt-4 text-center">
            <button
              type="button"
              disabled={isLoadingMore}
              onClick={() => void loadMore()}
              className="btn btn-outline px-4 py-2 text-sm disabled:opacity-50"
            >
              {isLoadingMore ? "Loading…" : "Load more notes"}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
